import { Grid, Autocomplete, useMediaQuery } from '@mui/material';
import { styled as muiStyled, css } from '@mui/material/styles';
import React from 'react';

import publicPolicyData from '../../../../public/static/public-policy-data.json';
import PublicPolicyCard from '../add-project-policy/policies/public-policy-lists';
import Arranger from '../base/arranger';
import { DesignToken } from '../base/core';
import Link from '../base/link';
import Spacer, { spc } from '../base/spacer';

import Text from '~c/components/base/text';
import { TextField } from '~c/components/base/text-field';

interface Props {
  className?: string;
}

const PublicPoliciesImpl: React.FC<Props> = ({ className }) => {
  const [searchKey, setSearchKey] = React.useState<any[]>([]);
  const prefersDarkMode = false;
  const filteredList = publicPolicyData.filter((policy) => {
    return searchKey.length ? searchKey.includes(policy._id) : true;
  });
  return (
    <div className={className}>
      <div className="grid-container">
        <Arranger direction="column">
          <Link href="/sign-up" rel="noopener">
            <img
              src={`${
                prefersDarkMode
                  ? `/images/new-logo-fairly-dark.png`
                  : `/images/new-logo-fairly-light.png`
              }`}
              height={36}
              alt="Fairly Logo"
            />
          </Link>
          <div className="vertical-line" />
        </Arranger>

        <Spacer pb={spc.l} />
        <Arranger direction="column" alignItems="center">
          <Text sz="h1">Fairly AI Policy Marketplace</Text>
          <Spacer pb={spc.s} />
          <Autocomplete
            multiple
            freeSolo
            filterSelectedOptions
            options={publicPolicyData.map((policy) => ({
              id: policy._id,
              label: policy.name,
            }))}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={searchKey.map((keyword) => ({
              id: keyword,
              label:
                publicPolicyData.find((policy) => policy._id === keyword)
                  ?.name ?? 'No label',
            }))}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.label
            }
            onChange={(event, value) => {
              setSearchKey(
                value.map((val) => (typeof val === 'string' ? val : val.id))
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                label="Search policy names"
                className="text-field-width"
              />
            )}
          />
        </Arranger>
        <Spacer pb={spc.s} />

        <Grid container spacing={4} className="policy-card">
          {filteredList.map((policy) => (
            <Grid key={policy._id} item>
              <PublicPolicyCard
                key={policy._id}
                policy={policy as any}
                className={className}
              />
            </Grid>
          ))}
        </Grid>

        <Spacer pb={spc.xxl} />
        <Arranger justify="center">
          {' '}
          <Text sz="body14">
            if you don't see what you need,{' '}
            <a href="mailto:info@fairly.ai" style={{ color: 'gray' }}>
              contact us
            </a>{' '}
            to develop a custom policy.
          </Text>
        </Arranger>
        <Spacer pb={spc.xxl} />
        <div className="vertical-line" />
        <Spacer pb={spc.xs} />
        <div className="footer">
          {' '}
          <Link href="/sign-up" rel="noopener">
            <img
              src={`${
                prefersDarkMode
                  ? `/images/new-logo-fairly-dark.png`
                  : `/images/new-logo-fairly-light.png`
              }`}
              height={36}
              alt="Fairly Logo"
            />
          </Link>
          <Text>© Copyright Fairly AI</Text>
          <Text>
            <a
              href="https://www.fairly.app/privacy"
              style={{ color: DesignToken.link }}
            >
              Privacy and Terms
            </a>
          </Text>
        </div>
      </div>
    </div>
  );
};

export const PublicPolicies = muiStyled(PublicPoliciesImpl)(
  ({ theme }) => css`
    .footer {
      display: flex;
      align-items: center;
      justify-content: space-between;
      @media (max-width: ${theme.breakpoints.values.md}px) {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
      }
    }
    .grid-container {
      padding: 20px 170px;
      @media (max-width: ${theme.breakpoints.values.sm}px) {
        padding: 20px 10px;
      }
      @media (max-width: ${theme.breakpoints.values.md}px) {
        padding: 20px 10px;
      }
    }
    .text-field-width {
      width: 600px;
      @media (max-width: ${theme.breakpoints.values.sm}px) {
        width: 320px;
      }
      @media (max-width: ${theme.breakpoints.values.md}px) {
        width: 350px;
      }
    }
    .vertical-line {
      height: 0.1px;
      width: auto;
      background-color: gray;
    }
    .policy-card {
      @media (max-width: ${theme.breakpoints.values.lg}px) {
        justify-content: flex-start;
      }
      @media (max-width: ${theme.breakpoints.values.md}px) {
        justify-content: center;
      }
    }
  `
);

export default PublicPolicies;
